import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui";
import MapView from "./MapView";
import EncuestaSocioambiental from "./EncuestaSocioambiental";
import MapLocationPicker from "./MapLocationPicker";
import { guardarRelacionObraBarrio } from '../services/obraBarrioService';
import { geocodeAddress, reverseGeocodePoint } from '../services/nominatimClient';
import { useTheme } from "../context/ThemeContext";
import { useGeo } from "../context/GeoContext";
import { useFormContext } from "../context/FormContext";
import { toast } from 'react-toastify';

const AREA_RESP_MICROCREDITOS = "ÁREA DE MICROCRÉDITOS";
const esNuevoBeneficiario = (b) => b && b._isNew === true;

function parseWKT(wkt) {
  const match = wkt && wkt.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/);
  if (!match) return null;
  return { lng: parseFloat(match[1]), lat: parseFloat(match[2]) };
}

const mapToEncuesta = (b) => {
    if (!b) return null;
    const revMapTenencia = { 0: 'propia', 1: 'cedida', 2: 'alquilada' };
    const revMapEstado = { 0: 'Bueno', 1: 'Regular', 2: 'Malo' };
    let criticidad = '';
    if (b.precaria_casilla) criticidad = 'Precaria/Casilla';
    else if (b.proceso_consolidacion) criticidad = 'En proceso de consolidación';
    else if (b.consolidada) criticidad = 'Consolidada';
    return {
        nombreApellido: b.nombre_beneficiario || '',
        nro_beneficiario: b.nro_beneficiario || '',
        fechaNacimiento: b.fecha_nacimiento ? String(b.fecha_nacimiento).substring(0, 10) : '',
        dni: b.documento || '',
        telefono: b.telefono || '',
        direccion: b.direccion || '',
        barrio: b.barrio || '',
        localidad: b.localidad || '',
        personasACargo: b.personas_a_cargo != null ? String(b.personas_a_cargo) : '',
        aniosEnBarrio: b.anios_en_barrio != null ? String(b.anios_en_barrio) : '',
        tipoVivienda: revMapTenencia[b.tenencia_vivienda] || '',
        convivientes: b.grupo_familiar && b.grupo_familiar.length > 0
            ? b.grupo_familiar
            : [{ nombre: '', relacion: '', edad: '', estudia: '', trabaja: '' }],
        nivelCriticidad: criticidad,
        estadoGeneral: revMapEstado[b.estado_general] || '',
        breveDescripcionMejora: b.descripcion || '',
        prioridades: {
            piso: !!b.prioridad_piso, techo: !!b.prioridad_techo, paredes: !!b.prioridad_paredes,
            revoques: !!b.prioridad_revoque, aislante: !!b.prioridad_aislante,
            instalacionGas: !!b.instalaciones_gas, instalacionAgua: !!b.instalaciones_agua,
            instalacionLuz: !!b.instalaciones_luz, instalacionCloacas: !!b.instalaciones_cloacas,
            otros: !!b.prioridad_otro,
        },
        calidadDeVidaMejora: b.mejora_calidad_vida || '',
        materiales: b.mejoras && b.mejoras.length > 0
            ? b.mejoras
            : [{ descripcion: '', cantidad: '', precio: '' },
               { descripcion: '', cantidad: '', precio: '' },
               { descripcion: '', cantidad: '', precio: '' }],
        manoDeObraPrecio: b.mano_obra != null ? String(b.mano_obra) : '',
        otroGastoPrecio: b.otro != null ? String(b.otro) : '',
        montoCredito: b.monto_a_solicitar != null ? String(b.monto_a_solicitar) : (b.monto || ''),
        formaPago: '',
        nroCuotas: b.cantidad_cuotas != null ? String(b.cantidad_cuotas) : '',
        montoCuotas: b.monto_cuotas != null ? String(b.monto_cuotas) : '',
        mesInicio: b.mes_inicio != null ? String(b.mes_inicio) : '',
        frecuenciaPago: b.frecuencia_pago != null ? String(b.frecuencia_pago) : '',
        garantiaGarantes: b.garantia_garantes || '',
        direccionDesdeMapa: b.direccionDesdeMapa || false,
    };
};

const Modal2 = ({ partido, areaResponsable, onFeatureDataChange, beneficiarios, onBeneficiariosChange, isOpen: externalOpen, onClose: externalClose, geoEditBeneficiario, editBeneficiario, showButton = true, renabapIds = '', rppvapCodigos = '', onAddTarget, onRemoveTarget = () => {}, onSetOtroTipo, onSetOtroNombre, onRemoveOtroTipo, idTablaObras, cg01IdPartidoObra }) => {
    const { clearFeatures } = useGeo();
    const { isDarkMode } = useTheme();
    const { formA } = useFormContext();
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
    const [mounted, setMounted] = useState(true);
    const [localFeatureData, setLocalFeatureData] = useState([]);
    const [savedFeatures, setSavedFeatures] = useState([]);
    const [pendienteAsignar, setPendienteAsignar] = useState(null);
    const [showBeneficiarioForm, setShowBeneficiarioForm] = useState(false);
    const [formInitialData, setFormInitialData] = useState(null);
    const [isNewMode, setIsNewMode] = useState(false);
    const [editandoBeneficiarioId, setEditandoBeneficiarioId] = useState(null);
    const [geoEditData, setGeoEditData] = useState(null);
    const [formDataGuardado, setFormDataGuardado] = useState(false);
    const [showModeSelector, setShowModeSelector] = useState(false);
    const [showMapLocationPicker, setShowMapLocationPicker] = useState(false);
    const [locationData, setLocationData] = useState(null);
    const [zoomToCoords, setZoomToCoords] = useState(null); // { lat, lng } para zoom automático tras geocoding
    const [retryQuery, setRetryQuery] = useState(''); // texto para re-intentar geocoding desde el mapa
    const [pendingGeoAddress, setPendingGeoAddress] = useState(null); // { wkt, direccion, localidad, barrio } de un punto dibujado esperando confirmación
    const [geocodingTargetInfo, setGeocodingTargetInfo] = useState(null); // { inTarget, nonTargets } resultado de checkTargetForWkt
    const [barriosReady, setBarriosReady] = useState(false);
    const [ubicacionModificada, setUbicacionModificada] = useState(false);
    // ID del beneficiario pendiente que se está re-editando (ida y vuelta encuesta <-> mapa)
    const [pendingEditId, setPendingEditId] = useState(null);
    // Indica si la ubicación del beneficiario activo ya fue confirmada (por dirección o clic en el mapa)
    const [ubicacionConfirmada, setUbicacionConfirmada] = useState(false);
    // Hay un punto colocado en el MapLocationPicker sin confirmar (estado interno del picker)
    const [pickerPuntoSinConfirmar, setPickerPuntoSinConfirmar] = useState(false);

    // Pre-fill retryQuery solo con la dirección (calle) del beneficiario pendiente.
    // La búsqueda por dirección se hace con la dirección de la encuesta; localidad/barrio
    // no deben precargarse en el input del mapa.
    useEffect(() => {
        if (pendienteAsignar) {
            const direccion = (pendienteAsignar.direccion || '').trim();
            if (direccion) setRetryQuery(direccion);
        }
    }, [pendienteAsignar?.id]);
    const retryInputRef = useRef(null);
    const mapViewRef = useRef(null);
    const encuestaFormDataRef = useRef(null);
    // Dirección tipeada en el mapa y confirmada (retryQuery): se propaga al formulario al confirmar
    const retryDireccionRef = useRef('');
    // Barrios auto-agregados a "seleccionados" por cada beneficiario al confirmar su punto
    // (id beneficiario -> [{ id, tipo }]). Se limpian al corregir/borrar la ubicación si no
    // quedan otros puntos dentro.
    const autoBarriosRef = useRef(new Map());
    const initialBeneficiarioIdsRef = useRef(new Set());

    // Barrios que se auto-agregan por el punto de un beneficiario: los no-target que lo contienen
    const getAutoBarriosDeWkt = useCallback((wkt) => {
        if (!wkt || !mapViewRef.current?.checkTargetForWkt) return [];
        const info = mapViewRef.current.checkTargetForWkt(wkt);
        if (!info || info.inTarget) return [];
        return info.nonTargets.map(nt => ({ id: nt.id, tipo: nt.tipo }));
    }, []);

    // Remover barrios auto-agregados por un beneficiario si ningún OTRO punto sigue dentro
    const limpiarBarriosAutoAgregados = useCallback((benefId, otrosBeneficiarios) => {
        // El ref solo sobrevive dentro de la misma sesión del modal; si se reabrió la ubicación
        // de un beneficiario pendiente (Editar geo) el registro persiste en _barriosAutoAgregados.
        const benefObj = (otrosBeneficiarios || []).find(b => b.id === benefId);
        const auto = autoBarriosRef.current.get(benefId) || benefObj?._barriosAutoAgregados || [];
        if (!auto?.length || !onRemoveTarget) return;
        // Si el mapa/capas de barrios no están listas no se puede verificar si otros puntos
        // siguen dentro: no se quita nada (se evita limpiar un barrio ocupado por error).
        if (!barriosReady || !mapViewRef.current?.findBarriosForWkt) return;
        const otros = (otrosBeneficiarios || []).filter(b => b.id !== benefId && b.wkt);
        auto.forEach(barrio => {
            const hayOtroPunto = otros.some(b => {
                const barrios = mapViewRef.current.findBarriosForWkt(b.wkt) || [];
                return barrios.some(x => String(x.id) === String(barrio.id) && x.tipo === barrio.tipo);
            });
            if (!hayOtroPunto) onRemoveTarget(barrio);
        });
        autoBarriosRef.current.delete(benefId);
    }, [onRemoveTarget, barriosReady]);

    // Re-verificar targets cuando barrios terminan de cargar
    const handleBarriosReady = useCallback(() => {
        setBarriosReady(true);
        if (pendienteAsignar?.wkt && mapViewRef.current?.checkTargetForWkt) {
            const info = mapViewRef.current.checkTargetForWkt(pendienteAsignar.wkt);
            setGeocodingTargetInfo(info);
        }
    }, [pendienteAsignar?.wkt]);

    // Verificar targets cuando cambia el WKT o cuando barrios se ponen ready
    useEffect(() => {
        if (!pendienteAsignar?.wkt) return;
        if (barriosReady && mapViewRef.current?.checkTargetForWkt) {
            const info = mapViewRef.current.checkTargetForWkt(pendienteAsignar.wkt);
            setGeocodingTargetInfo(info);
        }
    }, [pendienteAsignar?.wkt, barriosReady]);

    // Auto-asignar OTRO cuando el punto está fuera de todo polígono (geocoding automático),
    // y quitarlo si el punto (sin confirmar) cae dentro de un polígono (target o no-target),
    // salvo que ya haya otros beneficiarios con puntos marcados fuera de todo polígono.
    // Solo se procesa UNA vez por WKT. El reverse-geocoding para obtener el nombre del barrio
    // lo hace exclusivamente finalizarPunto (que recibe el geo ya resuelto desde el caller).
    // Este efecto solo ejecuta onSetOtroTipo para que el formulario marque "Otro", y delega
    // onSetOtroNombre a finalizarPunto para evitar una segunda llamada a OSM.
    const otroBarrioProcesadoRef = useRef(new Set());
    // Devuelve true si hay algún beneficiario (distinto de targetId) con un punto marcado
    // fuera de todo polígono (ni target ni no-target). Se usa para mantener "Otro" cuando
    // el punto actual cae dentro de un polígono pero otros puntos siguen fuera de todo.
    const hayPuntosFueraDeTodo = useCallback((targetId) => {
        if (!barriosReady || !mapViewRef.current?.checkTargetForWkt) return false;
        return (beneficiarios || []).some(b => {
            if (!b.wkt || b.id === targetId) return false;
            const info = mapViewRef.current.checkTargetForWkt(b.wkt);
            return !info.inTarget && info.nonTargets.length === 0;
        });
    }, [beneficiarios, barriosReady]);
    useEffect(() => {
        if (!geocodingTargetInfo || !pendienteAsignar?.wkt) return;
        const targetId = pendienteAsignar.id;
        const fueraDeTodo = !geocodingTargetInfo.inTarget && geocodingTargetInfo.nonTargets.length === 0;
        if (fueraDeTodo) {
            const wkt = pendienteAsignar.wkt;
            if (otroBarrioProcesadoRef.current.has(wkt)) return;
            otroBarrioProcesadoRef.current.add(wkt);
            if (onSetOtroTipo) onSetOtroTipo();
        } else if (ubicacionModificada) {
            // Punto dentro de un polígono sin confirmar: "Otro" no debería mantenerse
            // salvo que otros puntos estén marcados fuera de todo polígono.
            if (onRemoveOtroTipo && !hayPuntosFueraDeTodo(targetId)) {
                onRemoveOtroTipo();
            }
        }
        // Nota: onSetOtroNombre y el reverseGeocodePoint se manejan dentro de finalizarPunto
        // (que recibe el geo ya resuelto). Este efecto no ejecuta reverseGeocodePoint para
        // evitar llamadas duplicadas a OSM en el flujo de clic en el mapa.
    }, [geocodingTargetInfo, pendienteAsignar?.wkt, pendienteAsignar?.id, ubicacionModificada, onSetOtroTipo, onRemoveOtroTipo, hayPuntosFueraDeTodo]);

    const isMicrocreditos = areaResponsable === AREA_RESP_MICROCREDITOS;

    // Al entrar al mapa desde el listado o "Editar geo" con un beneficiario que tiene
    // dirección pero todavía sin punto, geocodificarla automáticamente (mismo criterio
    // que el flujo encuesta → mapa). Si no se encuentra, se deja sin punto para que se
    // ubique manualmente.
    const autoGeocodificarAlEntrar = useCallback(async (item) => {
        if (!item || item.wkt || !item.direccion?.trim()) return;
        const partidoNombre = formA?.datosGenerales?.partido_nombre;
        try {
            const coords = await geocodeAddress(item.direccion, item.localidad, item.barrio, partidoNombre);
            if (coords) {
                const actualizados = (beneficiarios || []).map(b =>
                    b.id === item.id ? { ...b, wkt: coords.wkt, _barriosAutoAgregados: undefined } : b
                );
                if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
                setZoomToCoords({ lat: coords.lat, lng: coords.lng });
                setPendienteAsignar({ ...item, wkt: coords.wkt });
                setUbicacionConfirmada(true);
                setUbicacionModificada(true);
                setHighlightBeneficiaryId(item.id);
            } else {
                toast.warning('No se encontró la dirección. Ubique al beneficiario manualmente en el mapa.');
                setPendienteAsignar(item);
                setZoomToCoords(null);
            }
        } catch {
            setPendienteAsignar(item);
            setZoomToCoords(null);
        }
    }, [beneficiarios, onBeneficiariosChange, formA]);

    // Al volver de la encuesta al mapa en los flujos de edición, replicar el comportamiento
    // de "Editar geo": zoom al punto (si existe) y dirección de la encuesta precargada en el
    // input de búsqueda. Si no hay punto pero sí dirección, se geocodifica automáticamente
    // usando "actualizados" (la lista con los datos recién guardados) para no pisarlos.
    const prepararMapaDesdeEncuesta = useCallback(async (conPunto, actualizados) => {
        setPendingEditId(null);
        setGeoEditData(conPunto);
        setPendienteAsignar(conPunto);
        setUbicacionConfirmada(!!conPunto.wkt);
        setUbicacionModificada(false);
        if (conPunto.wkt || !conPunto.direccion?.trim()) return;
        try {
            const partidoNombre = formA?.datosGenerales?.partido_nombre;
            const coords = await geocodeAddress(conPunto.direccion, conPunto.localidad, conPunto.barrio, partidoNombre);
            if (coords) {
                const withWkt = actualizados.map(b =>
                    b.id === conPunto.id ? { ...b, wkt: coords.wkt, _barriosAutoAgregados: undefined } : b
                );
                if (onBeneficiariosChange) onBeneficiariosChange(withWkt);
                setZoomToCoords({ lat: coords.lat, lng: coords.lng });
                setPendienteAsignar({ ...conPunto, wkt: coords.wkt });
                setUbicacionConfirmada(true);
                setUbicacionModificada(true);
                setHighlightBeneficiaryId(conPunto.id);
            } else {
                toast.warning('No se encontró la dirección. Ubique al beneficiario manualmente en el mapa.');
            }
        } catch {
            toast.warning('No se encontró la dirección. Ubique al beneficiario manualmente en el mapa.');
        }
    }, [formA, onBeneficiariosChange]);

    // Evita que el efecto de geoEdit se re-procese al cambiar la identidad de
    // autoGeocodificarAlEntrar (que depende de beneficiarios). Solo se auto-geocodifica
    // una vez por beneficiario abierto en "Editar geo".
    const geoEditAutoProcesadoRef = useRef(null);
    useEffect(() => {
        if (geoEditBeneficiario) {
            const copy = { ...geoEditBeneficiario };
            setGeoEditData(copy);
            setIsNewMode(false);
            if (geoEditAutoProcesadoRef.current !== geoEditBeneficiario.id) {
                geoEditAutoProcesadoRef.current = geoEditBeneficiario.id;
                setPendienteAsignar(copy);
                setUbicacionConfirmada(!!copy.wkt);
                setShowModeSelector(false);
                setShowBeneficiarioForm(false);
                setUbicacionModificada(false);
                autoGeocodificarAlEntrar(copy);
            }
        } else {
            geoEditAutoProcesadoRef.current = null;
            setGeoEditData(null);
            setPendienteAsignar(null);
        }
    }, [geoEditBeneficiario, autoGeocodificarAlEntrar]);

    useEffect(() => {
        if (editBeneficiario) {
            setFormInitialData(mapToEncuesta(editBeneficiario));
            setUbicacionConfirmada(!!editBeneficiario.wkt);
            setShowBeneficiarioForm(true);
            setShowModeSelector(false);
            setShowMapLocationPicker(false);
            setGeoEditData(null);
            setPendienteAsignar(null);
            setEditandoBeneficiarioId(editBeneficiario.id);
            setIsNewMode(false);
        }
    }, [editBeneficiario]);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Limpia el resaltado cuando se cierra la encuesta
    useEffect(() => {
        if (!showBeneficiarioForm) {
            setEditandoBeneficiarioId(null);
        }
    }, [showBeneficiarioForm]);

    const [highlightBeneficiaryId, setHighlightBeneficiaryId] = useState(null);
    // Resaltar beneficiario en el mapa cuando se selecciona para mapa o geo-edit
    useEffect(() => {
        setHighlightBeneficiaryId(pendienteAsignar?.id || null);
    }, [pendienteAsignar?.id]);

    useEffect(() => {
        if (isOpen) {
            // Snapshot de IDs existentes al abrir el modal (para no borrar del listado al cancelar)
            initialBeneficiarioIdsRef.current = new Set((beneficiarios || []).map(b => b.id));
        }
        if (isOpen && !geoEditBeneficiario && !editBeneficiario) {
            setShowModeSelector(true);
            setShowMapLocationPicker(false);
            setShowBeneficiarioForm(false);
            setLocationData(null);
            setFormDataGuardado(false);
            setUbicacionConfirmada(false);
            setPendingEditId(null);
        }
    }, [isOpen, geoEditBeneficiario, editBeneficiario]);

    const openModal = () => {
        if (externalOpen === undefined) setInternalOpen(true);
    };
    const closeModal = () => {
        if (externalOpen === undefined) {
            setInternalOpen(false);
        } else if (externalClose) {
            externalClose();
        }
    };

    const handleFeatureDataChange = (data) => {
        setLocalFeatureData(data);
        if (onFeatureDataChange) onFeatureDataChange(data);
    };

    const handleCancel = () => {
        clearFeatures();
        if (onFeatureDataChange) onFeatureDataChange([]);
        setLocalFeatureData([]);
        setSavedFeatures([]);
        // Remover barrios no target que se hayan agregado durante este flujo (si ya no hay puntos dentro)
        // En flujo Mapa->Encuesta el pendiente vive en pendingEditId tras "Validar e ir a datos".
        const pendienteId = pendienteAsignar?.id || pendingEditId;
        if (pendienteId) limpiarBarriosAutoAgregados(pendienteId, beneficiarios);
        // Eliminar beneficiario pendiente SOLO si se creó en esta sesión (no estaba al abrir el modal)
        if (pendienteId && !initialBeneficiarioIdsRef.current.has(pendienteId)) {
            const restantes = (beneficiarios || []).filter(b => b.id !== pendienteId);
            if (onBeneficiariosChange) onBeneficiariosChange(restantes);
        }
        setPendienteAsignar(null);
        setGeoEditData(null);
        setFormDataGuardado(false);
        setEditandoBeneficiarioId(null);
        setIsNewMode(false);
        setZoomToCoords(null);
        setPendingGeoAddress(null);
        setGeocodingTargetInfo(null);
        setShowModeSelector(false);
        setShowMapLocationPicker(false);
        setLocationData(null);
        setPendingEditId(null);
        setUbicacionConfirmada(false);
        closeModal();
    };

    const handleSaveAndClose = () => {
        // En el flujo Mapa -> Encuesta, si el beneficiario pendiente es nuevo y aún no se
        // completó la encuesta, no permitir cerrar "Validar y salir": hay que pasar por la
        // encuesta con "Validar e ir a datos" primero.
        const pendNuevo = pendienteAsignar && esNuevoBeneficiario(pendienteAsignar);
        if (pendNuevo && !(pendienteAsignar.nombre_beneficiario || '').trim()) {
            toast.warning('Primero complete los datos del beneficiario en la encuesta con "Validar e ir a datos".');
            return;
        }
        // Si aún no se creó ningún punto para el beneficiario, avisar antes de cerrar
        // (misma validación que en el flujo Encuesta primero).
        const hayPunto = !!(pendienteAsignar?.wkt || geoEditData?.wkt);
        const enFlujo = !showModeSelector && (showMapLocationPicker || !!pendienteAsignar || !!geoEditData || !!pendingEditId);
        if (enFlujo && !hayPunto) {
            if (!window.confirm('No se ha ubicado un punto para el beneficiario. ¿Desea salir realmente?')) return;
        }
        // Si hay un punto sin validar (nuevo o reubicado), preguntar antes de cerrar
        if (pendienteAsignar?.wkt && ubicacionModificada) {
            if (!window.confirm('Continuar sin validar la ubicación?')) return;
        }
        if (onFeatureDataChange) {
            setSavedFeatures(localFeatureData || []);
            onFeatureDataChange(localFeatureData || []);
        }
        if (geoEditData) {
            const actualizados = (beneficiarios || []).map(b =>
                b.id === geoEditData.id ? geoEditData : b
            );
            if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
        }
        setPendienteAsignar(null);
        setGeoEditData(null);
        setEditandoBeneficiarioId(null);
        setIsNewMode(false);
        setUbicacionConfirmada(false);
        closeModal();
    };

    // Hay datos que se perderían al cerrar/cancelar: punto asignado, geo-edit, edición en
    // curso, ida y vuelta, dirección por confirmar, ubicación modificada o formulario cargado.
    // Al ingresar por "Añadir beneficiarios" (Selector -> Datos/Mapa) todavía no hay nada que perder.
    const hayDatosPendientes = () => {
        const d = encuestaFormDataRef.current;
        const formConDatos = d && Object.keys(d).some(k => {
            const v = d[k];
            if (Array.isArray(v)) return v.some(item => item && Object.values(item).some(x => String(x).trim() !== ''));
            if (v && typeof v === 'object') return Object.values(v).some(x => Boolean(x));
            return String(v).trim() !== '';
        });
        return !!(pendienteAsignar || geoEditData || editandoBeneficiarioId || editBeneficiario || pendingEditId || pendingGeoAddress || ubicacionModificada || pickerPuntoSinConfirmar) || formConDatos;
    };

    // En el flujo Editar (datos o geo) desde el listado, al cancelar solo se alerta si hay
    // una ubicación sin validar (punto reubicado o dibujado sin confirmar). Los cambios de
    // formulario se descartan en silencio conservando los datos guardados.
    const hayUbicacionSinValidar = () => {
        if (pendingGeoAddress) return true;
        if (ubicacionModificada && (pendienteAsignar?.wkt || geoEditData?.wkt)) return true;
        return false;
    };

    const handleCancelWithConfirm = () => {
        // Flujos de edición desde el listado: solo alertar si hay una ubicación sin validar.
        if (geoEditData || editBeneficiario || editandoBeneficiarioId) {
            if (hayUbicacionSinValidar()) {
                if (!window.confirm('Hay una ubicación sin validar que se va a perder. ¿Salir?')) return;
            }
            handleCancel();
            return;
        }
        if (!hayDatosPendientes()) {
            handleCancel();
            return;
        }
        if (!window.confirm('Los datos ingresados se perderán. ¿Continuar?')) return;
        handleCancel();
    };

    // Cierre por la X del header. Solo avisa si hay datos pendientes de verdad.
    const handleHeaderClose = () => {
        if (showModeSelector) {
            closeModal();
            return;
        }
        if (geoEditData || editBeneficiario || editandoBeneficiarioId) {
            if (hayUbicacionSinValidar()) {
                if (!window.confirm('Hay una ubicación sin validar que se va a perder. ¿Salir?')) return;
            }
            handleCancel();
            return;
        }
        if (hayDatosPendientes()) {
            if (!window.confirm('Los datos ingresados se perderán. ¿Continuar?')) return;
            handleCancel();
        } else {
            closeModal();
        }
    };

    const handleVolverAEncuesta = () => {
        // La ubicación confirmada es la fuente de verdad para decidir el alert.
        // Si ya hay punto confirmado (por dirección o clic en el mapa), se vuelve
        // a la encuesta sin preguntar: el punto se mantiene.
        const currentId = pendienteAsignar?.id || geoEditData?.id || highlightBeneficiaryId;
        const currentBenef = currentId ? (beneficiarios || []).find(b => b.id === currentId) : null;
        const tienePunto = ubicacionConfirmada || !!(pendienteAsignar?.wkt || geoEditData?.wkt || currentBenef?.wkt);
        if (!tienePunto) {
            if (!window.confirm('No se ha ubicado un punto para el beneficiario. ¿Desea volver a la encuesta?')) return;
        }
        // Mantener el beneficiario pendiente y su geometría (si existe); solo volvemos a la encuesta
        setPendingEditId(pendienteAsignar?.id || geoEditData?.id || highlightBeneficiaryId || null);
        setPendienteAsignar(null);
        if (geoEditData) {
            // Geo-edit de un beneficiario existente: la encuesta se arma desde sus datos
            setFormInitialData(mapToEncuesta(currentBenef || geoEditData));
        } else {
            // Beneficiario pendiente nuevo: restaurar lo que se venía editando en el formulario
            const data = encuestaFormDataRef.current;
            if (data) {
                setFormInitialData({ ...data });
            }
        }
        setShowBeneficiarioForm(true);
    };

    const handleSwitchGeoEdit = useCallback((item) => {
        if (!geoEditData) return;
        if (item.id !== geoEditData.id) {
            setGeoEditData({ ...item });
            setPendienteAsignar(null);
            setUbicacionConfirmada(!!item.wkt);
        }
    }, [geoEditData]);

    const handleSeleccionarParaMapa = useCallback((item) => {
        // Si hay un formulario abierto con datos sin guardar, guardarlos primero
        if (showBeneficiarioForm && editandoBeneficiarioId && item.id) {
            const currentData = encuestaFormDataRef.current;
            if (currentData) {
                const currentBenef = (beneficiarios || []).find(b => b.id === editandoBeneficiarioId);
                if (currentBenef) {
                    const updated = mapFormToBeneficiario(currentData, currentBenef);
                    const actualizados = (beneficiarios || []).map(b =>
                        b.id === editandoBeneficiarioId ? { ...updated, wkt: b.wkt || updated.wkt } : b
                    );
                    if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
                }
            }
        }
        setShowModeSelector(false);
        setShowMapLocationPicker(false);
        setShowBeneficiarioForm(false);
        setPendienteAsignar(item);
        setUbicacionConfirmada(!!item.wkt);
        if (geoEditData && item.id !== geoEditData.id) {
            setGeoEditData({ ...item });
        }
        if (editandoBeneficiarioId) setEditandoBeneficiarioId(null);
        autoGeocodificarAlEntrar(item);
    }, [geoEditData, showBeneficiarioForm, editandoBeneficiarioId, beneficiarios, onBeneficiariosChange, autoGeocodificarAlEntrar]);

    const handleAgregarBeneficiario = () => {
        setShowModeSelector(true);
        setLocationData(null);
        setEditandoBeneficiarioId(null);
        setUbicacionConfirmada(false);
    };

    const handleModoFormFirst = () => {
        setShowModeSelector(false);
        setLocationData(null);
        setFormInitialData(null);
        setShowBeneficiarioForm(true);
        setIsNewMode(true);
        setEditandoBeneficiarioId(null);
        setUbicacionConfirmada(false);
        setPickerPuntoSinConfirmar(false);
    };

    const handleModoMapFirst = () => {
        setShowModeSelector(false);
        setShowMapLocationPicker(true);
        setLocationData(null);
        setFormInitialData(null);
        setShowBeneficiarioForm(false);
        setIsNewMode(true);
        setEditandoBeneficiarioId(null);
        setUbicacionConfirmada(false);
        setPickerPuntoSinConfirmar(false);
    };

    const handleLocationConfirm = (data) => {
        setShowMapLocationPicker(false);
        setLocationData(data);
        setFormInitialData({
            nombreApellido: '',
            nro_beneficiario: '',
            fechaNacimiento: '',
            dni: '',
            telefono: '',
            direccion: data.direccion || '',
            barrio: data.barrio || '',
            localidad: data.localidad || '',
            personasACargo: '',
            aniosEnBarrio: '',
            tipoVivienda: '',
            convivientes: [{ nombre: '', relacion: '', edad: '', estudia: '', trabaja: '' }],
            nivelCriticidad: '',
            estadoGeneral: '',
            breveDescripcionMejora: '',
            prioridades: {
                piso: false, techo: false, paredes: false, revoques: false,
                aislante: false, instalacionGas: false, instalacionAgua: false,
                instalacionLuz: false, instalacionCloacas: false, otros: false,
            },
            calidadDeVidaMejora: '',
            materiales: [
                { descripcion: '', cantidad: '', precio: '' },
                { descripcion: '', cantidad: '', precio: '' },
                { descripcion: '', cantidad: '', precio: '' },
            ],
            manoDeObraPrecio: '',
            otroGastoPrecio: '',
            montoCredito: '',
            formaPago: '',
            nroCuotas: '',
            montoCuotas: '',
            mesInicio: '',
            frecuenciaPago: '',
            garantiaGarantes: '',
        });
        setUbicacionConfirmada(true);
        setPickerPuntoSinConfirmar(false);
        // No auto-transición a la encuesta: se mantiene el mapa con el punto confirmado.
        // El usuario decide pasar al formulario con "Validar e ir a datos".
        const pendiente = {
            id: crypto.randomUUID(),
            _isNew: true,
            wkt: data.wkt,
            nombre_beneficiario: '',
            nro_beneficiario: null,
            documento: '',
            direccion: data.direccion || '',
            barrio: data.barrio || '',
            localidad: data.localidad || '',
        };
        setPendienteAsignar(pendiente);
        if (onBeneficiariosChange) onBeneficiariosChange([...(beneficiarios || []), pendiente]);
        setZoomToCoords({ lat: data.lat, lng: data.lng });
    };

    const handleAbrirEncuestaGeo = useCallback(() => {
        if (!geoEditData) return;
        setFormInitialData(mapToEncuesta(geoEditData));
        setShowBeneficiarioForm(true);
        setIsNewMode(false);
    }, [geoEditData]);

    const handleEditarDatosBeneficiario = useCallback((item) => {
        // Si ya hay un formulario abierto con otro beneficiario, guardar cambios primero
        if (showBeneficiarioForm && editandoBeneficiarioId && editandoBeneficiarioId !== item.id) {
            const currentData = encuestaFormDataRef.current;
            if (currentData) {
                const currentBenef = (beneficiarios || []).find(b => b.id === editandoBeneficiarioId);
                if (currentBenef) {
                    const updated = mapFormToBeneficiario(currentData, currentBenef);
                    const actualizados = (beneficiarios || []).map(b =>
                        b.id === editandoBeneficiarioId ? { ...updated, wkt: b.wkt || updated.wkt } : b
                    );
                    if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
                }
            }
        }
        setFormInitialData(mapToEncuesta(item));
        setShowBeneficiarioForm(true);
        setIsNewMode(false);
        setEditandoBeneficiarioId(item.id);
    }, [showBeneficiarioForm, editandoBeneficiarioId, beneficiarios, onBeneficiariosChange]);

    const handleBorrarBeneficiario = useCallback((itemId) => {
        if (pendienteAsignar?.id === itemId) {
            setPendienteAsignar(null);
            setUbicacionConfirmada(false);
        }
        const actualizados = (beneficiarios || []).filter(b => b.id !== itemId);
        if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
    }, [beneficiarios, onBeneficiariosChange, pendienteAsignar]);

    const mapFormToBeneficiario = useCallback((formData, existing) => {
        const mapTenencia = { propia: 0, cedida: 1, alquilada: 2 };
        const mapEstadoGeneral = { Bueno: 0, Regular: 1, Malo: 2 };
        const wktValue = existing?.wkt || locationData?.wkt || '';
        const esNuevo = !existing;
        return {
            id: existing?.id || crypto.randomUUID(),
            _isNew: esNuevo || (existing?._isNew === true),
            _barriosAutoAgregados: existing?._barriosAutoAgregados,
            nro_beneficiario: formData.nro_beneficiario ? parseInt(formData.nro_beneficiario) : null,
            nombre_beneficiario: formData.nombreApellido.trim(),
            documento: formData.dni.trim(),
            direccion: formData.direccion.trim(),
            telefono: formData.telefono.trim(),
            observaciones: existing?.observaciones || '',
            monto: formData.montoCredito || '',
            wkt: wktValue,
            fecha_nacimiento: formData.fechaNacimiento || null,
            barrio: formData.barrio || '',
            localidad: formData.localidad || '',
            personas_a_cargo: formData.personasACargo ? parseInt(formData.personasACargo) : null,
            anios_en_barrio: formData.aniosEnBarrio ? parseInt(formData.aniosEnBarrio) : null,
            tenencia_vivienda: mapTenencia[formData.tipoVivienda] ?? null,
            tenencia_otra: '',
            grupo_familiar: formData.convivientes || [],
            precaria_casilla: formData.nivelCriticidad === 'Precaria/Casilla',
            proceso_consolidacion: formData.nivelCriticidad === 'En proceso de consolidación',
            consolidada: formData.nivelCriticidad === 'Consolidada',
            estado_general: mapEstadoGeneral[formData.estadoGeneral] ?? null,
            descripcion: formData.breveDescripcionMejora || '',
            prioridad_piso: formData.prioridades.piso,
            prioridad_techo: formData.prioridades.techo,
            prioridad_paredes: formData.prioridades.paredes,
            prioridad_revoque: formData.prioridades.revoques,
            prioridad_aislante: formData.prioridades.aislante,
            prioridad_otro: formData.prioridades.otros ? 'Otros' : null,
            instalaciones_gas: formData.prioridades.instalacionGas,
            instalaciones_agua: formData.prioridades.instalacionAgua,
            instalaciones_luz: formData.prioridades.instalacionLuz,
            instalaciones_cloacas: formData.prioridades.instalacionCloacas,
            instalaciones_otras: '',
            mejora_calidad_vida: formData.calidadDeVidaMejora || '',
            mejoras: formData.materiales || [],
            mano_obra: formData.manoDeObraPrecio ? parseFloat(formData.manoDeObraPrecio) : null,
            otro: formData.otroGastoPrecio ? parseFloat(formData.otroGastoPrecio) : null,
            precio: null,
            monto_a_solicitar: formData.montoCredito ? parseFloat(formData.montoCredito) : null,
            monto_a_solicitar_texto: '',
            cantidad_cuotas: formData.nroCuotas ? parseInt(formData.nroCuotas) : null,
            monto_cuotas: formData.montoCuotas ? parseFloat(formData.montoCuotas) : null,
            mes_inicio: formData.mesInicio ? parseInt(formData.mesInicio) : null,
            frecuencia_pago: formData.frecuenciaPago ? parseInt(formData.frecuenciaPago) : null,
            garantia_garantes: formData.garantiaGarantes || '',
        };
    }, [locationData]);

    const handleGuardarDesdeEncuesta = useCallback(async (formData, action = 'ubicar') => {
        const esGeoEdit = !!geoEditData && !isNewMode;
        const pendingBase = pendingEditId ? (beneficiarios || []).find(b => b.id === pendingEditId) : null;
        // "editBase" es un beneficiario GUARDADO en edición (desde la tabla). pendingBase es un
        // pendiente del viaje de ida y vuelta encuesta <-> mapa, y NO debe cerrar el modal al
        // volver al mapa: esEditForm solo aplica a guardados reales.
        const editBase = editBeneficiario || (editandoBeneficiarioId ? (beneficiarios || []).find(b => b.id === editandoBeneficiarioId) : null);
        const existingBeneficiario = editBase || pendingBase;

        const esEditForm = !!editBase && !isNewMode;
        const esNuevoRegistro = !esGeoEdit && !esEditForm;

        // Campos obligatorios: nro beneficiario, nombre, dni (solo al crear/validar un registro nuevo)
        const nroRaw = formData.nro_beneficiario;
        const faltantesObligatorios = [
            !(nroRaw || '').toString().trim() ? 'N° beneficiario' : '',
            !(formData.nombreApellido || '').trim() ? 'Nombre' : '',
            !(formData.dni || '').trim() ? 'DNI' : '',
        ].filter(Boolean);
        if (esNuevoRegistro && faltantesObligatorios.length > 0) {
            toast.warning(`Complete los campos obligatorios que aún están vacíos: ${faltantesObligatorios.join(', ')}.`);
            return;
        }

        const nro = parseInt(nroRaw);
        if (nro && !isNaN(nro)) {
            const currentId = existingBeneficiario?.id || (esGeoEdit ? geoEditData?.id : null);
            const duplicado = (beneficiarios || []).find(b => b.nro_beneficiario === nro && b.id !== currentId);
            if (duplicado) {
                toast.warning(`El N° de beneficiario ${nro} ya está asignado a ${duplicado.nombre_beneficiario}`);
                return;
            }
        }

        const esMapFirst = !!locationData && isNewMode && !geoEditData;
        const nuevo = mapFormToBeneficiario(formData, (esGeoEdit || esEditForm) ? (geoEditData || existingBeneficiario) : (pendingBase || null));
        let actualizados;
        if (esGeoEdit) {
            actualizados = (beneficiarios || []).map(b => b.id === geoEditData.id ? { ...nuevo, wkt: b.wkt || nuevo.wkt } : b);
            if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
            if (onFeatureDataChange) {
                setSavedFeatures(localFeatureData || []);
                onFeatureDataChange(localFeatureData || []);
            }
            if (action === 'ubicar') {
                // "Validar e ir a mapa": volver al mapa con el comportamiento de "Editar geo"
                const conWkt = actualizados.find(b => b.id === nuevo.id);
                prepararMapaDesdeEncuesta({ ...nuevo, wkt: conWkt?.wkt || nuevo.wkt }, actualizados);
            } else {
                // "Validar y salir": guardar y cerrar el modal
                setPendingEditId(null);
                setPendienteAsignar(null);
                setGeoEditData(null);
                closeModal();
            }
        } else if (esEditForm) {
            actualizados = (beneficiarios || []).map(b => b.id === existingBeneficiario.id ? { ...nuevo, wkt: b.wkt || nuevo.wkt } : b);
            if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
            if (action === 'ubicar') {
                // "Validar e ir a mapa": volver al mapa con el comportamiento de "Editar geo"
                const conWkt = actualizados.find(b => b.id === nuevo.id);
                prepararMapaDesdeEncuesta({ ...nuevo, wkt: conWkt?.wkt || nuevo.wkt }, actualizados);
            } else {
                // "Validar y salir": guardar y cerrar el modal
                setPendingEditId(null);
                setPendienteAsignar(null);
                closeModal();
            }
        } else {
            // Re-guardar un pendiente (ida y vuelta encuesta <-> mapa) o crear uno nuevo
            actualizados = pendingBase
                ? (beneficiarios || []).map(b => b.id === pendingBase.id ? { ...nuevo, wkt: b.wkt || nuevo.wkt } : b)
                : [...(beneficiarios || []), nuevo];
            if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
            setPendingEditId(null);
            setPendienteAsignar(null);
            if (action === 'validar') {
                toast.success('Datos validados correctamente');
                closeModal();
                setLocationData(null);
                setFormInitialData(null);
                setIsNewMode(false);
                setEditandoBeneficiarioId(null);
                setShowBeneficiarioForm(false);
                return;
            }
        }
        if (action === 'validar') return;
        setLocationData(null);
        setFormInitialData(null);
        setIsNewMode(false);
        setEditandoBeneficiarioId(null);
        setShowBeneficiarioForm(false);
        if (esMapFirst) {
            // Flujo Mapa -> Encuesta: el punto ya fue confirmado por el picker, no se re-geocodifica.
            // Mantener el pendiente (y su pendingEditId) para el viaje de ida y vuelta encuesta <-> mapa.
            setPendienteAsignar(nuevo);
            setPendingEditId(nuevo.id);
            setUbicacionConfirmada(true);
        } else if (!esGeoEdit && !esEditForm) {
            if (pendingBase?.wkt) {
                // Ida y vuelta con punto ya confirmado: si la dirección no cambió se conserva el
                // punto sin re-geocodificar ni re-zoom; si cambió, se re-busca la nueva dirección.
                const dirAnterior = [pendingBase.direccion, pendingBase.localidad, pendingBase.barrio].map(v => (v || '').trim()).join('|');
                const dirNueva = [formData.direccion, formData.localidad, formData.barrio].map(v => (v || '').trim()).join('|');
                if (dirAnterior === dirNueva) {
                    setPendienteAsignar(nuevo);
                    setUbicacionConfirmada(true);
                } else {
                    // La dirección cambió: el punto viejo deja de cubrir los barrios que auto-agregó
                    limpiarBarriosAutoAgregados(pendingBase.id, beneficiarios);
                    try {
                        const partidoNombre = formA?.datosGenerales?.partido_nombre;
                        const coords = await geocodeAddress(formData.direccion, formData.localidad, formData.barrio, partidoNombre);
                        if (coords) {
                            const withWkt = actualizados.map(b =>
                                b.id === nuevo.id ? { ...b, wkt: coords.wkt, _barriosAutoAgregados: undefined } : b
                            );
                            if (onBeneficiariosChange) onBeneficiariosChange(withWkt);
                            setZoomToCoords({ lat: coords.lat, lng: coords.lng });
                            setPendienteAsignar({ ...nuevo, wkt: coords.wkt });
                            setUbicacionConfirmada(true);
                            setUbicacionModificada(true);
                            setHighlightBeneficiaryId(nuevo.id);
                        } else {
                            // La nueva dirección no se pudo geocodificar: sin punto, ubicar manualmente
                            const sinWkt = actualizados.map(b =>
                                b.id === nuevo.id ? { ...b, wkt: '', _barriosAutoAgregados: undefined } : b
                            );
                            if (onBeneficiariosChange) onBeneficiariosChange(sinWkt);
                            setPendienteAsignar({ ...nuevo, wkt: '' });
                            setUbicacionConfirmada(false);
                            setZoomToCoords(null);
                            mapViewRef.current?.zoomToTargets?.();
                            toast.warning('La dirección cambió pero no se pudo encontrar. Ubique al beneficiario manualmente en el mapa.');
                        }
                    } catch {
                        const sinWkt = actualizados.map(b =>
                            b.id === nuevo.id ? { ...b, wkt: '', _barriosAutoAgregados: undefined } : b
                        );
                        if (onBeneficiariosChange) onBeneficiariosChange(sinWkt);
                        setPendienteAsignar({ ...nuevo, wkt: '' });
                        setUbicacionConfirmada(false);
                        setZoomToCoords(null);
                        mapViewRef.current?.zoomToTargets?.();
                    }
                }
            } else if (!formData.direccion?.trim()) {
                setPendienteAsignar(nuevo);
                setUbicacionConfirmada(false);
            } else {
                try {
                    const partidoNombre = formA?.datosGenerales?.partido_nombre;
                    const coords = await geocodeAddress(formData.direccion, formData.localidad, formData.barrio, partidoNombre);
                        if (coords) {
                        const withWkt = actualizados.map(b =>
                            b.id === nuevo.id ? { ...b, wkt: coords.wkt, _barriosAutoAgregados: undefined } : b
                        );
                        if (onBeneficiariosChange) onBeneficiariosChange(withWkt);
                        setZoomToCoords({ lat: coords.lat, lng: coords.lng });
                        setPendienteAsignar({ ...nuevo, wkt: coords.wkt });
                        setUbicacionConfirmada(true);
                        setUbicacionModificada(true);
                        setHighlightBeneficiaryId(nuevo.id);
                        // El chequeo de target barrio se hace via efecto cuando barriosReady=true
                    } else {
                        setPendienteAsignar(nuevo);
                        setUbicacionConfirmada(false);
                        setZoomToCoords(null);
                        mapViewRef.current?.zoomToTargets?.();
                        toast.warning('No se encontró la dirección. Ubique al beneficiario manualmente en el mapa.');
                    }
                } catch {
                    setPendienteAsignar(nuevo);
                    setUbicacionConfirmada(false);
                    setZoomToCoords(null);
                    mapViewRef.current?.zoomToTargets?.();
                }
            }
        }
    }, [beneficiarios, onBeneficiariosChange, geoEditData, editBeneficiario, editandoBeneficiarioId, isNewMode, mapFormToBeneficiario, onFeatureDataChange, localFeatureData, locationData, formA, formDataGuardado, pendingEditId, limpiarBarriosAutoAgregados, prepararMapaDesdeEncuesta]);

    const seleccionarSiguientePendiente = useCallback((beneficiariosActualizados) => {
        const sinPunto = (beneficiariosActualizados || []).find(b => !b.wkt);
        if (sinPunto) {
            setPendienteAsignar(sinPunto);
        } else {
            setPendienteAsignar(null);
        }
    }, []);

    const handlePuntoDibujado = useCallback(async (wkt) => {
        setZoomToCoords(null);
        if (!pendienteAsignar) return;
        setUbicacionModificada(true);
        const coords = parseWKT(wkt);
        if (!coords) { finalizarPunto(wkt); return; }
        let geoOsm = null;
        try {
            geoOsm = await reverseGeocodePoint(coords.lat, coords.lng);
        } catch {
            // ignorar
        }
        const direccionOsm = geoOsm?.direccion || '';
        // Pasar el geo ya obtenido para evitar una segunda llamada a OSM para el barrio Otro
        finalizarPunto(wkt, geoOsm);
        if (direccionOsm) {
            setRetryQuery(direccionOsm);
            retryDireccionRef.current = direccionOsm;
        }
    }, [pendienteAsignar]);

    const finalizarPunto = useCallback((wkt, geoOsm) => {
        if (!pendienteAsignar) return;
        // Un punto dibujado reemplaza cualquier dirección tipeada pendiente de aplicar
        retryDireccionRef.current = '';
        const targetId = pendienteAsignar.id;
        // Limpiar barrios auto-agregados de la ubicación anterior antes de reubicar
        limpiarBarriosAutoAgregados(targetId, beneficiarios);
        if (mapViewRef.current?.checkTargetForWkt) {
            const info = mapViewRef.current.checkTargetForWkt(wkt);
            setGeocodingTargetInfo(info);
            if (!info.inTarget && info.nonTargets.length === 0) {
                if (!otroBarrioProcesadoRef.current.has(wkt)) {
                    otroBarrioProcesadoRef.current.add(wkt);
                    if (onSetOtroTipo) onSetOtroTipo();
                    if (onSetOtroNombre) {
                        if (geoOsm?.barrio) {
                            onSetOtroNombre(geoOsm.barrio);
                            alert(`Se agregó "${geoOsm.barrio}" como barrio "Otro".`);
                        } else if (!geoOsm) {
                            // Solo si no se pudo obtener ningún dato de OSM en el primer intento
                            const coords = parseWKT(wkt);
                            if (coords) {
                                reverseGeocodePoint(coords.lat, coords.lng).then(geo => {
                                    if (geo?.barrio) {
                                        onSetOtroNombre(geo.barrio);
                                        alert(`Se agregó "${geo.barrio}" como barrio "Otro".`);
                                    } else {
                                        alert('No se pudo obtener el nombre del barrio desde OpenStreetMap.');
                                    }
                                });
                            }
                        } else {
                            alert('No se pudo obtener el nombre del barrio desde OpenStreetMap.');
                        }
                    }
                }
            }
        }
        setHighlightBeneficiaryId(targetId);
        if (geoEditData && targetId === geoEditData.id) {
            setGeoEditData(prev => ({ ...prev, wkt }));
        }
        const actualizados = (beneficiarios || []).map(b =>
            b.id === targetId ? { ...b, wkt, _barriosAutoAgregados: undefined } : b
        );
        if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
        // Mantener el beneficiario confirmado como activo (naranja) hasta guardar en BD.
        // No saltar a otro pendiente sin punto: eso cambiaba el contexto al nombre de otro
        // beneficiario y el alert de "No se ha ubicado un punto" aparecía erróneamente.
        setPendienteAsignar(prev => (prev && prev.id === targetId) ? { ...prev, wkt } : prev);
        setUbicacionConfirmada(true);
        setPendingGeoAddress(null);
    }, [pendienteAsignar, beneficiarios, onBeneficiariosChange, geoEditData, onSetOtroTipo, onSetOtroNombre, limpiarBarriosAutoAgregados]);

    // Confirmar dirección geolocalizada desde el punto dibujado: usar la encontrada
    const handleUsarDireccionGeolocalizada = useCallback(() => {
        if (!pendingGeoAddress || !pendienteAsignar) return;
        if (mapViewRef.current?.addOutsideTargets) {
            mapViewRef.current.addOutsideTargets(pendingGeoAddress.wkt);
        }
        const targetId = pendienteAsignar.id;
        const autoBarrios = getAutoBarriosDeWkt(pendingGeoAddress.wkt);
        autoBarriosRef.current.set(targetId, autoBarrios);
        setHighlightBeneficiaryId(targetId);
        if (geoEditData && targetId === geoEditData.id) {
            setGeoEditData(prev => ({ ...prev, wkt: pendingGeoAddress.wkt }));
        }
        const { wkt, direccion, localidad, barrio } = pendingGeoAddress;
        const updated = { ...pendienteAsignar, wkt, direccion, localidad, barrio, direccionDesdeMapa: true, _barriosAutoAgregados: autoBarrios };
        const actualizados = (beneficiarios || []).map(b =>
            b.id === targetId ? updated : b
        );
        if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
        // Sincronizar también el formulario de la encuesta para que al volver muestre la dirección encontrada
        if (encuestaFormDataRef.current) {
            encuestaFormDataRef.current = { ...encuestaFormDataRef.current, direccion, localidad, barrio, direccionDesdeMapa: true };
        }
        // Mantener pendienteAsignar activo (naranja) hasta guardar en BD (incluye el wkt)
        setPendienteAsignar(updated);
        setPendingGeoAddress(null);
        setUbicacionModificada(false);
        setUbicacionConfirmada(true);
    }, [pendingGeoAddress, pendienteAsignar, beneficiarios, onBeneficiariosChange, geoEditData, getAutoBarriosDeWkt]);

    // Confirmar dirección: mantener la ingresada originalmente, solo actualizar WKT
    const handleMantenerDireccion = useCallback(() => {
        if (!pendingGeoAddress || !pendienteAsignar) return;
        if (mapViewRef.current?.addOutsideTargets) {
            mapViewRef.current.addOutsideTargets(pendingGeoAddress.wkt);
        }
        const targetId = pendienteAsignar.id;
        const autoBarrios = getAutoBarriosDeWkt(pendingGeoAddress.wkt);
        autoBarriosRef.current.set(targetId, autoBarrios);
        setHighlightBeneficiaryId(targetId);
        if (geoEditData && targetId === geoEditData.id) {
            setGeoEditData(prev => ({ ...prev, wkt: pendingGeoAddress.wkt }));
        }
        const actualizados = (beneficiarios || []).map(b =>
            b.id === targetId ? { ...b, wkt: pendingGeoAddress.wkt, _barriosAutoAgregados: autoBarrios } : b
        );
        if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
        // Mantener pendienteAsignar activo (naranja) hasta guardar en BD (incluye el wkt)
        setPendienteAsignar(prev => (prev && prev.id === targetId) ? { ...prev, wkt: pendingGeoAddress.wkt } : prev);
        setPendingGeoAddress(null);
        setUbicacionModificada(false);
        setUbicacionConfirmada(true);
    }, [pendingGeoAddress, pendienteAsignar, beneficiarios, onBeneficiariosChange, geoEditData, getAutoBarriosDeWkt]);

    // Confirmar ubicación: guarda el WKT pero mantiene el punto como activo (naranja)
    // porque aún no está persistido en BD (se guarda al "Validar y salir")
    const handleConfirmarUbicacion = useCallback(() => {
        if (!pendienteAsignar || !pendienteAsignar.wkt) return;
        const retryDir = retryDireccionRef.current.trim();
        if (geocodingTargetInfo && !geocodingTargetInfo.inTarget && geocodingTargetInfo.nonTargets.length > 0) {
            if (mapViewRef.current?.addOutsideTargets) {
                mapViewRef.current.addOutsideTargets(pendienteAsignar.wkt);
            }
        }
        const target = pendienteAsignar;
        const targetId = target.id;
        const autoBarrios = getAutoBarriosDeWkt(target.wkt);
        autoBarriosRef.current.set(targetId, autoBarrios);
        setHighlightBeneficiaryId(targetId);
        // En modo geoEdit mantener sync geoEditData para que handleSaveAndClose no lo sobrescriba
        if (geoEditData && targetId === geoEditData.id) {
            setGeoEditData(prev => ({ ...prev, wkt: target.wkt }));
        }
        const actualizados = (beneficiarios || []).map(b =>
            b.id === targetId ? { ...b, wkt: target.wkt, _barriosAutoAgregados: autoBarrios, ...(retryDir ? { direccion: retryDir } : {}) } : b
        );
        if (onBeneficiariosChange) onBeneficiariosChange(actualizados);
        // Si el usuario tipeó una nueva dirección en el mapa, propagarla al formulario de la encuesta
        if (retryDir) {
            if (encuestaFormDataRef.current) {
                encuestaFormDataRef.current = { ...encuestaFormDataRef.current, direccion: retryDir, direccionDesdeMapa: true };
            }
            setPendienteAsignar(prev => (prev && prev.id === targetId) ? { ...prev, direccion: retryDir } : prev);
            retryDireccionRef.current = '';
        }
        // No limpiar pendienteAsignar: el punto sigue activo (naranja) hasta guardar en BD.
        // El input de dirección conserva la dirección confirmada (no se limpia).
        setZoomToCoords(null);
        setGeocodingTargetInfo(null);
        setUbicacionModificada(false);
        setUbicacionConfirmada(true);
    }, [pendienteAsignar, beneficiarios, onBeneficiariosChange, geocodingTargetInfo, geoEditData, getAutoBarriosDeWkt]);

    // Cancelar punto geocodificado: limpia wkt y vuelve a estado inicial para re-intentar
    const handleCancelarGeocoding = useCallback(() => {
        if (!pendienteAsignar) return;
        const targetId = pendienteAsignar.id;
        limpiarBarriosAutoAgregados(targetId, beneficiarios);
        setHighlightBeneficiaryId(null);
        const sinWkt = (beneficiarios || []).map(b =>
            b.id === targetId ? { ...b, wkt: '', _barriosAutoAgregados: undefined } : b
        );
        if (onBeneficiariosChange) onBeneficiariosChange(sinWkt);
        setPendienteAsignar({ ...pendienteAsignar, wkt: '' });
        setUbicacionConfirmada(false);
        setRetryQuery('');
        retryDireccionRef.current = '';
        setGeocodingTargetInfo(null);
        setUbicacionModificada(false);
        setZoomToCoords(null);
        // Al cancelar se limpia también la dirección del formulario de la encuesta para que no
        // quede la del punto descartado (en el viaje de ida y vuelta encuesta <-> mapa).
        if (encuestaFormDataRef.current) {
            encuestaFormDataRef.current = { ...encuestaFormDataRef.current, direccion: '', direccionDesdeMapa: false };
        }
    }, [pendienteAsignar, beneficiarios, onBeneficiariosChange, limpiarBarriosAutoAgregados]);

    // Re-intentar geocoding desde el mapa (input overlay)
    const handleRetryGeocode = useCallback(async () => {
        const target = pendienteAsignar;
        if (!target || !retryQuery.trim()) return;
        const partidoNombre = formA?.datosGenerales?.partido_nombre;
        try {
            const coords = await geocodeAddress(retryQuery, '', '', partidoNombre);
            if (coords) {
                // El punto viejo queda sin puntos dentro de los barrios que auto-agregó
                limpiarBarriosAutoAgregados(target.id, beneficiarios);
                setUbicacionModificada(true);
                retryDireccionRef.current = retryQuery.trim();
                const withWkt = (beneficiarios || []).map(b =>
                    b.id === target.id ? { ...b, wkt: coords.wkt, _barriosAutoAgregados: undefined } : b
                );
                if (onBeneficiariosChange) onBeneficiariosChange(withWkt);
                setZoomToCoords({ lat: coords.lat, lng: coords.lng });
                setPendienteAsignar({ ...target, wkt: coords.wkt });
                setUbicacionConfirmada(true);
                // Resaltar el punto encontrado igual que al colocarlo con clic en el mapa
                setHighlightBeneficiaryId(target.id);
                // Chequear target
                if (mapViewRef.current?.checkTargetForWkt) {
                    const info = mapViewRef.current.checkTargetForWkt(coords.wkt);
                    setGeocodingTargetInfo(info);
                    // Si está fuera de todo polígono, asignar Otro (el efecto ya no hace
                    // reverseGeocodePoint; lo manejamos aquí usando coords para no duplicar).
                    if (!info.inTarget && info.nonTargets.length === 0 && !otroBarrioProcesadoRef.current.has(coords.wkt)) {
                        otroBarrioProcesadoRef.current.add(coords.wkt);
                        if (onSetOtroTipo) onSetOtroTipo();
                        if (onSetOtroNombre) {
                            const coord = parseWKT(coords.wkt);
                            if (coord) {
                                reverseGeocodePoint(coord.lat, coord.lng).then(geo => {
                                    if (geo?.barrio) {
                                        onSetOtroNombre(geo.barrio);
                                        alert(`Se agregó "${geo.barrio}" como barrio "Otro".`);
                                    } else {
                                        alert('No se pudo obtener el nombre del barrio desde OpenStreetMap.');
                                    }
                                });
                            }
                        }
                    }
                }
            } else {
                toast.warning('No se encontró la dirección con ese término.');
                setGeocodingTargetInfo(null);
            }
        } catch {
            toast.error('Error al buscar la dirección.');
            setGeocodingTargetInfo(null);
        }
    }, [pendienteAsignar, retryQuery, beneficiarios, onBeneficiariosChange, formA, limpiarBarriosAutoAgregados, onSetOtroTipo, onSetOtroNombre]);

    const beneficiaryFeatures = (beneficiarios || [])
        .filter(b => b.wkt)
        .map(b => ({ id: b.id, wkt: b.wkt, label: b.nombre_beneficiario }));

    // Info de target/no-target del punto activo (se reusa en el overlay de búsqueda y en el de dirección)
    const targetInfoBlock = geocodingTargetInfo ? (
        <div className={`mt-2 rounded p-2 text-xs ${geocodingTargetInfo.inTarget ? 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200' : geocodingTargetInfo.nonTargets.length > 0 ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200' : 'bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'}`}>
            {geocodingTargetInfo.inTarget ? (
                <p className="font-semibold">Está dentro del barrio seleccionado</p>
            ) : geocodingTargetInfo.nonTargets.length > 0 ? (
                <>
                    <p className="font-semibold">Fuera de barrio seleccionado</p>
                    <ul className="list-disc list-inside mt-1">
                        {geocodingTargetInfo.nonTargets.map((nt, i) => (
                            <li key={i}><span className="font-semibold">{nt.nombre}</span> ({nt.tipo})</li>
                        ))}
                    </ul>
                    <p className="mt-1">Se agregará a barrios seleccionados: {[...new Set(geocodingTargetInfo.nonTargets.map(nt => nt.tipo))].join(', ')}.</p>
                </>
            ) : (
                <p className="font-semibold">Fuera de todo polígono</p>
            )}
        </div>
    ) : null;

    if (!mounted) return null;

    return (
        <div>
            {showButton && (
                <Button
                    onClick={openModal}
                    disabled={!partido || partido === 0 || partido === '0'}
                    className={`px-4 py-2 rounded text-white ${!partido || partido === 0 || partido === '0' ? 'bg-blue-500 opacity-50 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                    Mapeo de las áreas de intervención
                </Button>
            )}

            {isOpen && (
                <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDarkMode ? 'bg-black/70' : 'bg-black/50'}`} onClick={closeModal}>
                    <div
                        className={`w-[90vw] h-[90vh] rounded-lg shadow-xl flex flex-col overflow-hidden transition-colors duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className={`p-3 border-b flex items-center gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <h2 className={`text-lg font-bold flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {isMicrocreditos ? 'Mapa de Beneficiarios - Microcréditos' : 'Mapa del Proyecto'}
                            </h2>
                            {pendienteAsignar && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                    {ubicacionConfirmada
                                        ? <span className="text-green-600 dark:text-green-400 font-medium">Punto asignado a: {pendienteAsignar.nombre_beneficiario}</span>
                                        : <>Asignando punto para: {pendienteAsignar.nombre_beneficiario}</>}
                                </span>
                            )}
                            <button
                                onClick={handleHeaderClose}
                                className={`p-1.5 rounded-full hover:bg-opacity-20 hover:bg-gray-500 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
                                title="Cerrar"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-grow flex overflow-hidden">
                            {/* Sidebar beneficiarios — ocultado temporalmente */}
                            {false && (
                                <div className={`w-72 flex-shrink-0 flex flex-col min-h-0 border-r ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="p-3 border-b flex items-center justify-between">
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                            Beneficiarios ({(beneficiarios || []).length})
                                        </h3>
                                    </div>

                                        <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                            {geoEditData && (
                                                <div className="p-2 mb-1">
                                                    <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Haga doble clic para editar datos de la encuesta
                                                    </p>
                                                </div>
                                            )}
                                            {(beneficiarios || []).length === 0 && !showBeneficiarioForm && (
                                                <div className="text-center py-8">
                                                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                                                        No hay beneficiarios
                                                    </p>
                                                    <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        Presione "+ Nuevo" para agregar
                                                    </p>
                                                </div>
                                            )}
                                            {(() => {
                                                const nuevos = (beneficiarios || []).filter(esNuevoBeneficiario);
                                                const existentes = (beneficiarios || []).filter(b => !esNuevoBeneficiario(b)).sort((a, b) => {
                                                    const na = a.nro_beneficiario, nb = b.nro_beneficiario;
                                                    const numA = na != null && na !== '' ? Number(na) : NaN;
                                                    const numB = nb != null && nb !== '' ? Number(nb) : NaN;
                                                    if (!isNaN(numA) && isNaN(numB)) return -1;
                                                    if (isNaN(numA) && !isNaN(numB)) return 1;
                                                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                                    return 0;
                                                });
                                                return (
                                                    <>
                                                    {nuevos.length > 0 && (
                                                        <div className="px-2 pt-1 pb-0.5">
                                                            <span className="text-[10px] font-semibold text-yellow-600 uppercase tracking-wider">Pendientes</span>
                                                        </div>
                                                    )}
                                                    {nuevos.map((item, index) => (
                                                    <div
                                                        key={item.id || `nuevo-${index}`}
                                                      className={`flex items-center justify-between p-2 rounded text-xs cursor-pointer transition-colors ${
                                                              pendienteAsignar?.id === item.id || editandoBeneficiarioId === item.id
                                                                  ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-400'
                                                                  : item.wkt
                                                                      ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                                      : 'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'
                                                          } ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                                                          onClick={() => !item.wkt && handleSeleccionarParaMapa(item)}
                                                          onDoubleClick={() => {
                                                              if (geoEditData) {
                                                                  if (item.id !== geoEditData.id) {
                                                                      handleSwitchGeoEdit(item);
                                                                  }
                                                              }
                                                          }}
                                                       >
                                                     <div className="flex-1 min-w-0">
                                                                <div className="font-medium truncate">
                                                                    {item.nro_beneficiario != null && <span className="text-gray-400 mr-1 font-mono text-[10px]">#{item.nro_beneficiario}</span>}
                                                                    {item.nombre_beneficiario}
                                                                </div>
                                                                <div className="flex items-center gap-2 text-[10px]">
                                                                    {item.documento && <span>{item.documento}</span>}
                                                                    {item.wkt
                                                                        ? <span className="text-green-600 font-medium">✓ Ubicado</span>
                                                                        : <span className="text-orange-500 font-medium">— Sin punto</span>
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 ml-1 shrink-0">
                                                                   <button
                                                                       onClick={(e) => { e.stopPropagation(); handleSeleccionarParaMapa(item); }}
                                                                       className={`text-[10px] px-1.5 py-0.5 border rounded font-medium ${
                                                                           pendienteAsignar?.id === item.id
                                                                               ? 'text-orange-700 bg-orange-100 border-orange-400 dark:text-orange-300 dark:bg-orange-900/30 dark:border-orange-700'
                                                                               : 'text-blue-600 hover:text-blue-800 border-blue-300 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                                                       }`}
                                                                   >
                                                                       {item.wkt ? 'Editar geo' : 'Agregar geo'}
                                                                   </button>
                                                                   <button
                                                                       onClick={(e) => { e.stopPropagation(); handleEditarDatosBeneficiario(item); }}
                                                                       className={`text-[10px] px-1.5 py-0.5 border rounded font-medium ${
                                                                           editandoBeneficiarioId === item.id
                                                                               ? 'text-purple-700 bg-purple-100 border-purple-300 dark:text-purple-300 dark:bg-purple-900/30 dark:border-purple-700'
                                                                               : 'text-green-600 hover:text-green-800 border-green-300 dark:text-green-400 dark:hover:text-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                                       }`}
                                                                   >
                                                                       Datos
                                                                   </button>
                                                                   {esNuevoBeneficiario(item) && (
                                                                      <button
                                                                          onClick={(e) => { e.stopPropagation(); handleBorrarBeneficiario(item.id); }}
                                                                          className="text-red-600 hover:text-red-800 text-[10px] px-1.5 py-0.5 border border-red-300 rounded font-medium"
                                                                      >
                                                                          Borrar
                                                                      </button>
                                                                  )}
                                                               </div>
                                                         </div>
                                                        ))}
                                                        {nuevos.length > 0 && existentes.length > 0 && (
                                                           <div className="border-t border-gray-200 dark:border-gray-700 mx-2 my-1" />
                                                       )}
                                                       {existentes.length > 0 && (
                                                           <div className="px-2 pt-1 pb-0.5">
                                                               <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Guardados</span>
                                                          </div>
                                                      )}
                                                      {existentes.map((item, index) => (
                                                      <div
                                                          key={item.id || `exist-${index}`}
                                                         className={`flex items-center justify-between p-2 rounded text-xs cursor-pointer transition-colors ${
                                                                 pendienteAsignar?.id === item.id || editandoBeneficiarioId === item.id
                                                                     ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-400'
                                                                     : item.id === geoEditData?.id
                                                                         ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                                                                         : item.wkt
                                                                             ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                                             : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                                             } ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                                                           onClick={() => !item.wkt && handleSeleccionarParaMapa(item)}
                                                           onDoubleClick={() => {
                                                               if (geoEditData) {
                                                                   if (item.id !== geoEditData.id) {
                                                                       handleSwitchGeoEdit(item);
                                                                   }
                                                               }
                                                           }}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-medium truncate">
                                                                    {item.nro_beneficiario != null && <span className="text-gray-400 mr-1 font-mono text-[10px]">#{item.nro_beneficiario}</span>}
                                                                    {item.nombre_beneficiario}
                                                                </div>
                                                                <div className="flex items-center gap-2 text-[10px]">
                                                                    {item.documento && <span>{item.documento}</span>}
                                                                    {item.wkt
                                                                        ? <span className="text-green-600 font-medium">✓ Ubicado</span>
                                                                        : <span className="text-orange-500 font-medium">— Sin punto</span>
                                                                    }
                                                                </div>
                                                            </div>
                                                              <div className="flex items-center gap-1 ml-1 shrink-0">
                                                                   <button
                                                                       onClick={(e) => { e.stopPropagation(); handleSeleccionarParaMapa(item); }}
                                                                       className={`text-[10px] px-1.5 py-0.5 border rounded font-medium ${
                                                                           pendienteAsignar?.id === item.id
                                                                               ? 'text-orange-700 bg-orange-100 border-orange-400 dark:text-orange-300 dark:bg-orange-900/30 dark:border-orange-700'
                                                                               : 'text-blue-600 hover:text-blue-800 border-blue-300 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                                                       }`}
                                                                   >
                                                                       {item.wkt ? 'Editar geo' : 'Agregar geo'}
                                                                   </button>
                                                                   <button
                                                                       onClick={(e) => { e.stopPropagation(); handleEditarDatosBeneficiario(item); }}
                                                                       className={`text-[10px] px-1.5 py-0.5 border rounded font-medium ${
                                                                           editandoBeneficiarioId === item.id
                                                                               ? 'text-purple-700 bg-purple-100 border-purple-300 dark:text-purple-300 dark:bg-purple-900/30 dark:border-purple-700'
                                                                               : 'text-green-600 hover:text-green-800 border-green-300 dark:text-green-400 dark:hover:text-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                                       }`}
                                                                   >
                                                                       Datos
                                                                   </button>
                                                              </div>
                                            </div>
                                                    ))}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                </div>
                            )}

                            {/* Content area: mode selector / location picker / form / map */}
                            <div className="flex-grow relative">
                                {/* MapView siempre montado: al alternar encuesta <-> mapa se conserva la vista/zoom.
                                    Se oculta (invisible) cuando el selector, picker o formulario están arriba. */}
                                {mounted && (
                                    <div className={`absolute inset-0 ${showModeSelector || showMapLocationPicker || showBeneficiarioForm ? 'invisible' : ''}`}>
                                        <MapView
                                            ref={mapViewRef}
                                            onClose={() => {}}
                                            areaResponsable={areaResponsable}
                                            onFeatureDataChange={handleFeatureDataChange}
                                            initialFeatures={savedFeatures}
                                            pendingBeneficiarioId={pendienteAsignar?.id}
                                            pendingBeneficiarioNombre={pendienteAsignar?.nombre_beneficiario}
                                            onBeneficiaryPointPlaced={handlePuntoDibujado}
                                            onOutsideTargetConfirm={onAddTarget}
                                            onSetOtroTipo={onSetOtroTipo}
                                            onSetOtroNombre={onSetOtroNombre}
                                            beneficiaryFeatures={beneficiaryFeatures}
                                            zoomToBeneficiaryId={geoEditData?.wkt ? geoEditData.id : pendienteAsignar?.wkt ? pendienteAsignar.id : null}
                                            highlightBeneficiaryId={highlightBeneficiaryId}
                                            zoomToCoords={zoomToCoords}
                                            renabapIds={renabapIds}
                                            rppvapCodigos={rppvapCodigos}
                                            geoEditMode={!!geoEditData}
                                            disableDrawing={!!pendingGeoAddress}
                                            onBarriosReady={handleBarriosReady}
                                            noAutoZoom={!!pendingEditId}
                                        />

                                            {/* Overlay de geocoding: siempre visible mientras se asigna un punto */}
                                            {pendienteAsignar && !pendingGeoAddress && (
                                                <div className="absolute top-3 right-3 z-30 w-[36rem] max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3">
                                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                                                        {pendienteAsignar.wkt
                                                            ? 'Punto ubicado en el mapa. Ingresá otra dirección si querés reubicarlo:'
                                                            : 'Ingresá una dirección para ubicar al beneficiario o hacé clic en el mapa:'}
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <input
                                                            ref={retryInputRef}
                                                            type="text"
                                                            value={retryQuery}
                                                            onChange={(e) => {
                                                                setRetryQuery(e.target.value);
                                                                // Si el usuario edita el input, la dirección precargada deja de aplicarse al confirmar
                                                                retryDireccionRef.current = '';
                                                            }}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleRetryGeocode()}
                                                            placeholder="Ej: Calle, Localidad"
                                                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <button
                                                            onClick={handleRetryGeocode}
                                                            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                                                        >
                                                            Buscar
                                                        </button>
                                                    </div>
                                                    {/* La info de target/no-target solo se muestra mientras la ubicación está pendiente de confirmar */}
                                                    {pendienteAsignar.wkt && ubicacionModificada && targetInfoBlock}
                                                    {pendienteAsignar.wkt && ubicacionModificada && (
                                                        <div className="mt-2 flex gap-2">
                                                            <button
                                                                onClick={handleCancelarGeocoding}
                                                                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors"
                                                            >
                                                                Cancelar
                                                            </button>
                                                            <button
                                                                onClick={handleConfirmarUbicacion}
                                                                className="flex-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded font-medium"
                                                            >
                                                                Confirmar ubicación y continuar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Confirmación de dirección geolocalizada desde clic en el mapa */}
                                            {pendingGeoAddress && (
                                                <div className="absolute top-3 right-3 z-30 w-[36rem] max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3">
                                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                                        Dirección encontrada:
                                                    </p>
                                                    <p className="text-sm text-gray-900 dark:text-gray-100 mb-2.5 bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                                        {pendingGeoAddress.direccion}
                                                    </p>
                                                    {targetInfoBlock}
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                                        {pendienteAsignar?.direccion?.trim()
                                                            ? '¿Usar esta dirección para la encuesta o mantener la ingresada originalmente?'
                                                            : '¿Usar esta dirección para la encuesta?'}
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={handleUsarDireccionGeolocalizada}
                                                            className="flex-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                                                        >
                                                            Usar dirección encontrada
                                                        </button>
                                                        <button
                                                            onClick={handleMantenerDireccion}
                                                            className="flex-1 px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded font-medium"
                                                        >
                                                            {pendienteAsignar?.direccion?.trim()
                                                                ? 'Mantener dirección ingresada'
                                                                : 'Continuar sin dirección'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                )}

                                {showModeSelector ? (
                                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white dark:bg-gray-800">
                                        <div className="max-w-lg w-full px-6">
                                            <h3 className={`text-lg font-bold text-center mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                ¿Cómo desea registrar al beneficiario?
                                            </h3>
                                            <div className="space-y-4">
                                                <button
                                                    onClick={handleModoFormFirst}
                                                    className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                                                        isDarkMode
                                                            ? 'border-gray-600 hover:border-blue-500 bg-gray-700 text-gray-100'
                                                            : 'border-gray-200 hover:border-blue-500 bg-white text-gray-800'
                                                    }`}
                                                >
                                                    <p className="font-semibold text-sm">Completar datos primero</p>
                                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Complete la encuesta y luego ubique al beneficiario en el mapa
                                                    </p>
                                                </button>
                                                <button
                                                    onClick={handleModoMapFirst}
                                                    className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                                                        isDarkMode
                                                            ? 'border-gray-600 hover:border-green-500 bg-gray-700 text-gray-100'
                                                            : 'border-gray-200 hover:border-green-500 bg-white text-gray-800'
                                                    }`}
                                                >
                                                    <p className="font-semibold text-sm">Ubicar en mapa primero</p>
                                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Marque la ubicación en el mapa y la dirección se cargará automáticamente
                                                    </p>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {showMapLocationPicker ? (
                                    <div className="absolute inset-0 z-10">
                                        <MapLocationPicker
                                            onConfirm={handleLocationConfirm}
                                            onPendingChange={setPickerPuntoSinConfirmar}
                                            renabapIds={renabapIds}
                                            rppvapCodigos={rppvapCodigos}
                                            onOutsideTargetConfirm={onAddTarget}
                                            onSetOtroTipo={onSetOtroTipo}
                                            onSetOtroNombre={onSetOtroNombre}
                                            beneficiaryFeatures={beneficiaryFeatures}
                                        />
                                    </div>
                                ) : null}
                                {showBeneficiarioForm ? (
                                    <div className="absolute inset-0 z-10 overflow-y-auto bg-white dark:bg-gray-800">
                                    <EncuestaSocioambiental
                                        key={editandoBeneficiarioId}
                                        initialData={editBeneficiario ? mapToEncuesta(editBeneficiario) : formInitialData}
                                        onSave={handleGuardarDesdeEncuesta}
                                        onCancel={handleCancelWithConfirm}
                                        saveLabel={geoEditData || editBeneficiario || editandoBeneficiarioId ? 'Validar e ir a mapa' : undefined}
                                        showUbicarButton={true}
                                        formDataRef={encuestaFormDataRef}
                                    />
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`p-3 flex justify-end items-center gap-2 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                            {!showBeneficiarioForm && (
                                <>
                                    {showModeSelector ? (
                                        <>
                                            <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors">
                                                Cerrar
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={handleCancelWithConfirm} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors">
                                                Cancelar
                                            </button>
                                            <button onClick={handleVolverAEncuesta} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors">
                                                Validar e ir a datos
                                            </button>
                                            <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors">
                                                Validar y salir
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Modal2;
